import GameCanvas from '../components/GameCanvas';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getAvatarOption } from '../game/avatar/AvatarCatalog';
import { UserRound, LogOut } from 'lucide-react';

function StudentHomeAvatar({ student, avatarBase, onStudentExit, exitBusy }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const exitRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  useLayoutEffect(() => {
    if (!open) return;
    const placeMenu = () => {
      const root = rootRef.current;
      const menu = menuRef.current;
      if (!root || !menu) return;
      const box = root.getBoundingClientRect();
      const menuBox = menu.getBoundingClientRect();
      const brandBox = root.closest('.tmag-home')?.querySelector('.tmag-brand')?.getBoundingClientRect();
      const left = box.right - menuBox.width;
      let top = box.bottom + 8;
      // Only move the dropdown; never change the approved home composition.
      if (brandBox && left < brandBox.right && box.right > brandBox.left &&
          top < brandBox.bottom && top + menuBox.height > brandBox.top) top = brandBox.bottom + 8;
      menu.style.top = `${top - box.top}px`;
    };
    placeMenu();
    window.addEventListener('resize', placeMenu);
    return () => window.removeEventListener('resize', placeMenu);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const closeOutside = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('focusin', closeOutside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('focusin', closeOutside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  useEffect(() => { if (open && !exitBusy) exitRef.current?.focus(); }, [open, exitBusy]);
  return <div className="tmag-student-avatar" ref={rootRef}>
    <button className={`tmag-student-avatar-trigger${getAvatarOption('bases', avatarBase) ? ' has-astronaut' : ''}`} type="button" ref={triggerRef}
      aria-label={`Abrir menú de ${student.alias}`} aria-haspopup="menu" aria-expanded={open} aria-controls={menuId}
      onClick={() => setOpen(value => !value)}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault(); setOpen(true); exitRef.current?.focus();
        }
      }}>
      {getAvatarOption('bases', avatarBase) ? <img src={getAvatarOption('bases', avatarBase).portraitAsset} alt="" /> : <UserRound aria-hidden="true" />}
    </button>
    {open && <div className="tmag-student-avatar-menu" ref={menuRef} id={menuId} role="menu" aria-label={`Perfil de ${student.alias}`}
      onKeyDown={event => {
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault(); exitRef.current?.focus();
        }
      }}>
      <p role="presentation" title={student.alias}>{student.alias}</p>
      <button type="button" role="menuitem" ref={exitRef} onClick={onStudentExit} disabled={exitBusy} aria-busy={exitBusy}>
        <LogOut aria-hidden="true" /><span>{exitBusy ? 'Saliendo…' : 'Salir del perfil'}</span>
      </button>
    </div>}
  </div>;
}

export default function GamePage({ student, onStudentExit, exitBusy = false }) {
  const frameRef = useRef(null);
  const [identityHost, setIdentityHost] = useState(null);
  const studentId = student?.student_id;
  useEffect(() => {
    const frame = frameRef.current;
    if (!studentId || !frame) return;
    // MenuScene owns its layout; React keeps the existing protected exit callback.
    // Observe only this game's slot, never another mounted student's surface.
    const syncIdentity = () => {
      const next = frame.querySelector('.tmag-student-identity-slot');
      setIdentityHost(current => current === next ? current : next);
    };
    const observer = new MutationObserver(syncIdentity);
    observer.observe(frame, { childList: true, subtree: true });
    syncIdentity();
    return () => observer.disconnect();
  }, [studentId]);
  return (
    <div className="tmag-page" data-testid="game-page">
      <div className="tmag-frame" ref={frameRef}>
        <GameCanvas student={student} />
        {student && identityHost && createPortal(
          <StudentHomeAvatar key={studentId} student={student} avatarBase={identityHost.dataset.studentId === studentId ? identityHost.dataset.avatarBase : null} onStudentExit={onStudentExit} exitBusy={exitBusy} />, identityHost,
        )}
      </div>
    </div>
  );
}
